import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Save, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { translations as defaultTranslations } from '../../i18n/translations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTranslations() {
  const [overrides, setOverrides] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [edited, setEdited] = useState({});
  const token = localStorage.getItem('itcs-admin-token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/admin/translations`, { headers }).then(r => {
      const map = {};
      (r.data || []).forEach(item => { if (item.key) map[item.key] = { de: item.de, en: item.en, es: item.es }; });
      setOverrides(map);
    }).catch(() => {});
  }, []);

  // Build all keys from default translations
  const allKeys = Object.keys(defaultTranslations.de).sort();
  const filteredKeys = allKeys.filter(k => k.toLowerCase().includes(searchTerm.toLowerCase()) || 
    defaultTranslations.de[k]?.toLowerCase().includes(searchTerm.toLowerCase()));

  const getValue = (key, lang) => {
    if (edited[key]?.[lang] !== undefined) return edited[key][lang];
    if (overrides[key]?.[lang]) return overrides[key][lang];
    return defaultTranslations[lang]?.[key] || '';
  };

  const handleEdit = (key, lang, value) => {
    setEdited(prev => ({
      ...prev,
      [key]: { ...prev[key], de: getValue(key, 'de'), en: getValue(key, 'en'), es: getValue(key, 'es'), [lang]: value }
    }));
  };

  const handleSave = async () => {
    if (Object.keys(edited).length === 0) return;
    try {
      await axios.put(`${API}/admin/translations`, { translations: edited }, { headers });
      setOverrides(prev => ({ ...prev, ...edited }));
      setEdited({});
      toast.success(`${Object.keys(edited).length} Übersetzungen gespeichert`);
    } catch { toast.error('Fehler beim Speichern'); }
  };

  return (
    <div data-testid="admin-translations">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Übersetzungen verwalten</h1>
          <p className="text-sm text-slate-500 mt-1">Bearbeiten Sie alle Texte in Deutsch, Englisch und Spanisch</p>
        </div>
        <Button onClick={handleSave} disabled={Object.keys(edited).length === 0} className="bg-[#066ce7] text-white" data-testid="save-translations-btn">
          <Save className="w-4 h-4 mr-2" /> Speichern ({Object.keys(edited).length})
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Suche nach Schlussel oder Text..." className="pl-10" data-testid="translation-search" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="max-h-[60vh] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="sticky top-0 bg-white z-10">
                <TableHead className="w-48">Schlüssel</TableHead>
                <TableHead>Deutsch</TableHead>
                <TableHead>Englisch</TableHead>
                <TableHead>Spanisch</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredKeys.map(key => (
                <TableRow key={key} className={edited[key] ? 'bg-blue-50/50' : ''} data-testid={`translation-row-${key}`}>
                  <TableCell className="font-mono text-xs text-slate-500 align-top pt-3">
                    {key}
                    {edited[key] && <Badge className="ml-1 bg-amber-100 text-amber-700 text-[10px]">geändert</Badge>}
                  </TableCell>
                  {['de', 'en', 'es'].map(lang => (
                    <TableCell key={lang} className="align-top">
                      <Input
                        value={getValue(key, lang)}
                        onChange={e => handleEdit(key, lang, e.target.value)}
                        className="text-xs h-8"
                        data-testid={`translation-input-${key}-${lang}`}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-3">{filteredKeys.length} von {allKeys.length} Übersetzungen</p>
    </div>
  );
}
